/*
    🔥 API SERVICES
        Capa de acceso HTTP reutilizable para los módulos de la aplicación.
        Todas las operaciones respetan API_BASE.HAS_CONNECT antes de ejecutar
        una petición real.

    ----------------------------------------------------------------------------
    🔥 BACKEND DESHABILITADO
        Cuando REACT_APP_BACKEND_HAS_CONNECT=false no se realiza ninguna
        petición HTTP. Las funciones devuelven una respuesta normalizada para
        que vistas y hooks puedan tratar el estado como backend no disponible
        sin generar conexiones accidentales.

    ----------------------------------------------------------------------------
    🔥 OPERACIONES
        GET_AUTHENTICATED_ME_USER
            Obtiene el usuario autenticado.

        POST_RESOURCE / UPDATE_resource / PATCH_resource / DELETE_resource
            Ejecutan mutaciones y notifican al sistema para refrescar datos
            dependientes cuando corresponde.

        GET_RESOURCES / GET_RESOURCE
            Ejecutan consultas de listas o recursos individuales.
*/

import API_CONNECT from './ApiConfig';
import { API_BASE } from './_ApiBase';
import { HANDLE_AXIOS_ERROR } from '../_ApiUtil/handleAxiosError';
import { ADMIN_ACTION_VERIFICATION } from '../_Security/AdminActionVerification';

const BACKEND_DISABLED_RESPONSE = () => ({
    success: false,
    status: 503,
    code_status: 503,
    data: null,
    message: 'Backend deshabilitado por configuración.',
    detail: null,
    isOffline: true,
    isBackendDisabled: true,
});

const CAN_CONNECT = () => API_BASE.HAS_CONNECT === true;
const WITH_ADMIN_VERIFICATION = async (method, resource, config = {}) => {
    if (!ADMIN_ACTION_VERIFICATION.isProtectedResource(resource)) {
        return { config, actionToken: null };
    }
    const actionToken = await ADMIN_ACTION_VERIFICATION.requestToken({ method, resource });
    if (actionToken === false) return { config, actionToken: null };
    if (!actionToken) return null;

    // V235: el token viaja dentro del body de la mutación y no en un header
    // personalizado. Así evitamos que CORS bloquee el PATCH/PUT/DELETE por
    // X-Admin-Action-Token antes de que llegue al backend.
    return { config, actionToken };
};


const EMIT_APP_NOTIFICATIONS_REFRESH = () => {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new Event('scool-data:notifications-refresh'));
};

export const API_SERVICES = {
    GET_AUTHENTICATED_ME_USER: async (resource, token) => {
        if (!CAN_CONNECT()) return BACKEND_DISABLED_RESPONSE();
        try {
            const RESPONSE = await API_CONNECT.get(`${resource}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            return RESPONSE.data;
        } catch (ERROR) {
            return HANDLE_AXIOS_ERROR(ERROR);
        }
    },

    POST_RESOURCE: async (resource, data, config = {}) => {
        if (!CAN_CONNECT()) return BACKEND_DISABLED_RESPONSE();
        try {
            const RESPONSE = await API_CONNECT.post(`${resource}`, data, config);
            EMIT_APP_NOTIFICATIONS_REFRESH();
            return RESPONSE.data;
        } catch (ERROR) {
            return HANDLE_AXIOS_ERROR(ERROR);
        }
    },

    UPDATE_resource: async (resource, id, data, config = {}) => {
        if (!CAN_CONNECT()) return BACKEND_DISABLED_RESPONSE();
        try {
            const URL_RESOURCE = `${resource}${id ? "/" + id : ""}`;
            const VERIFIED = await WITH_ADMIN_VERIFICATION('PUT', URL_RESOURCE, config);
            if (!VERIFIED) return { success: false, status: 499, code_status: 499, code: 'ADMIN_ACTION_CANCELLED', data: null, message: 'Acción cancelada.' };
            const BODY = VERIFIED.actionToken && data && typeof data === 'object' && !Array.isArray(data)
                ? { ...data, _adminActionToken: VERIFIED.actionToken }
                : data;
            const RESPONSE = await API_CONNECT.put(URL_RESOURCE, BODY, VERIFIED.config);
            EMIT_APP_NOTIFICATIONS_REFRESH();
            return RESPONSE.data;
        } catch (ERROR) {
            return HANDLE_AXIOS_ERROR(ERROR);
        }
    },

    PATCH_resource: async (resource, ...args) => {
        if (!CAN_CONNECT()) return BACKEND_DISABLED_RESPONSE();
        try {
            const HAS_ID = typeof args[0] === "string" || typeof args[0] === "number";
            const ID = HAS_ID ? args[0] : null;
            const DATA = HAS_ID ? args[1] : args[0];
            const CONFIG = (HAS_ID ? args[2] : args[1]) ?? {};
            const URL_RESOURCE = `${resource}${ID != null && ID !== "" ? `/${ID}` : ""}`;

            const VERIFIED = await WITH_ADMIN_VERIFICATION('PATCH', URL_RESOURCE, CONFIG);
            if (!VERIFIED) return { success: false, status: 499, code_status: 499, code: 'ADMIN_ACTION_CANCELLED', data: null, message: 'Acción cancelada.' };
            const BASE_BODY = DATA && typeof DATA === 'object' && !Array.isArray(DATA) ? DATA : {};
            const BODY = VERIFIED.actionToken
                ? { ...BASE_BODY, _adminActionToken: VERIFIED.actionToken }
                : (DATA ?? null);
            const RESPONSE = await API_CONNECT.patch(URL_RESOURCE, BODY, VERIFIED.config);
            EMIT_APP_NOTIFICATIONS_REFRESH();
            return RESPONSE.data;
        } catch (ERROR) {
            return HANDLE_AXIOS_ERROR(ERROR);
        }
    },

    DELETE_resource: async (resource, id, data = null, config = {}) => {
        if (!CAN_CONNECT()) return BACKEND_DISABLED_RESPONSE();
        try {
            const URL_RESOURCE = `${resource}/${id}`;
            const VERIFIED = await WITH_ADMIN_VERIFICATION('DELETE', URL_RESOURCE, config);
            if (!VERIFIED) return { success: false, status: 499, code_status: 499, code: 'ADMIN_ACTION_CANCELLED', data: null, message: 'Acción cancelada.' };
            const BASE_BODY = data && typeof data === 'object' && !Array.isArray(data) ? data : {};
            const BODY = VERIFIED.actionToken ? { ...BASE_BODY, _adminActionToken: VERIFIED.actionToken } : data;
            const RESPONSE = await API_CONNECT.delete(URL_RESOURCE, { ...VERIFIED.config, data: BODY });
            EMIT_APP_NOTIFICATIONS_REFRESH();
            return RESPONSE.data;
        } catch (ERROR) {
            return HANDLE_AXIOS_ERROR(ERROR);
        }
    },

    GET_RESOURCES: async (resource, params = {}, config = {}) => {
        if (!CAN_CONNECT()) return BACKEND_DISABLED_RESPONSE();
        try {
            const RESPONSE = await API_CONNECT.get(`${resource}`, { params, ...config });
            return RESPONSE.data;
        } catch (ERROR) {
            return HANDLE_AXIOS_ERROR(ERROR);
        }
    },

    GET_RESOURCE: async (resource, config = {}) => {
        if (!CAN_CONNECT()) return BACKEND_DISABLED_RESPONSE();
        try {
            const RESPONSE = await API_CONNECT.get(`${resource}`, config);
            return RESPONSE.data;
        } catch (ERROR) {
            return HANDLE_AXIOS_ERROR(ERROR);
        }
    },
};
